import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Zap, Trash2, Plus, Store, Truck, FileText,
    DollarSign, Calendar, Percent, CreditCard,
    ChevronLeft, Loader2, Package, Calculator, Info,
    AlertCircle
} from 'lucide-react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import DateTimeInput from '../components/ui/DateTimeInput';
import TextArea from '../components/ui/TextArea';
import Checkbox from '../components/ui/Checkbox';
import FastAPIClient from '../services/apiClient';
import { useToast } from '../hooks/useToast';
import {
    fetchStores, fetchStoresWhereStaffAssigned, fetchVendors,
    generateUniquePoNumber, fetchCurrencies, fetchAllBankAccounts,
    fetchVendorCredit,
    fetchProductsForStoreSimple
} from '../apiHelper';

const client = new FastAPIClient();

const initialLineItem = {
    catentry_id: '',
    quantity: '',
    cost_price: '',
    discount_type: 'percent', // 'percent' or 'amount'
    discount_val: 0,
    vat_percent: 0,
    freight_cost: 0,
    selling_price: '',
    expiry_date: '',
    comment: '',
    // UI Intel
    prev_cost: 0,
    product_name: '',
    is_promo: false,
    promo_value: '',
};

const FastTrackRestock = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const location = useLocation();
    const vendorId = location.state?.vendor_id;

    const [user, setUser] = useState(null);
    const [stores, setStores] = useState([]);
    const [vendorDetails, setVendorDetails] = useState(null);
    const [vendors, setVendors] = useState([]);
    const [catentries, setCatentries] = useState([]);
    const [currencies, setCurrencies] = useState([]);
    const [bankAccounts, setBankAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [header, setHeader] = useState({
        store_id: '',
        vendor_id: vendorId || '',
        ponumber: generateUniquePoNumber(),
        receipt_date: new Date().toISOString().substring(0, 16),
        currency: '',
        payment_method: 'cash',
        invoice_due_date: '',
    });

    const [lineItems, setLineItems] = useState([{ ...initialLineItem }]);
    const [payments, setPayments] = useState([{ bankaccount_id: '', amount: '' }]);

    // Add to your state declarations
    const [availableCredit, setAvailableCredit] = useState(0);
    const [isFetchingCredit, setIsFetchingCredit] = useState(false);

    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) { navigate('/login'); return; }
        const parsedUser = JSON.parse(lcUser);
        setUser(parsedUser);

        const loadData = async () => {
            try {
                const [vList, sList, cList, bList] = await Promise.all([
                    fetchVendors(parsedUser),
                    // fetchStores(parsedUser),
                    fetchStoresWhereStaffAssigned(parsedUser),
                    fetchCurrencies(parsedUser),
                    fetchAllBankAccounts()
                ]);

                // const currentVendor = vList.find(v => v.vendor_id === vendorId);
                setVendors(vList);
                // setVendorDetails(currentVendor);
                setStores(sList.map(s => ({ label: s.identifier, value: s.store_id })));
                setCurrencies(cList.map(c => ({ label: `${c.setccurr} (${c.setcnote})`, value: c.setccurr })));
                setBankAccounts(bList.map(b => ({ label: b.name, value: b.account_id })));

                if (vendorId) {
                    const current = vList.find(v => v.vendor_id == parseInt(vendorId));
                    if (current) setVendorDetails(current)
                }
                if (cList.length > 0) setHeader(h => ({ ...h, currency: cList[0].setccurr }));
            } catch (err) {
                showToast("Initialization failed", "error");
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [vendorId, navigate, showToast]);

    // useEffect(() => {
    //     if (header.store_id && user) {
    //         fetchProductsForOrg()
    //             .then(data => setCatentries(data.map(i => ({
    //                 label: `${i.productname} (${i.partnumber})`,
    //                 value: i.catentry_id,
    //                 data: i
    //             }))));
    //     }
    // }, [header.store_id, user]);

    useEffect(() => {
        if (!header.store_id || !user) return;

        const payload = {
            users_id: user.users_id,
            logon_id: user.logon_id,
            store_id: parseInt(header.store_id),
            offset: 0,
            limit: 100000
        }
        // console.log(payload)
        fetchProductsForStoreSimple(payload)
            .then(data => {
                // console.log(data)
                setCatentries(data.map(i => ({
                    label: `${i.productname} (${i.partnumber})`,
                    value: i.catentry_id,
                    data: i
                })));
            })
            .catch(err => {
                console.error("Failed to load products: ", err);
            });
    }, [header.store_id, user])

    useEffect(() => {
        const hasPromo = lineItems.some(item => item.is_promo == true);
        if (hasPromo && header.payment_method !== 'credit') {
            setHeader(prev => ({ ...prev, payment_method: 'credit' }));
            showToast("Payment mode changed to Credit due to promo items. Consider recording promo items solely without other items", "info", 5000);
        }
    }, [lineItems, header.payment_method, showToast]);

    const calculateItemTotals = (item) => {
        const base = parseFloat(item.cost_price) || 0;
        const freight = parseFloat(item.freight_cost) || 0;

        const disc = item.discount_type === 'percent'
            ? base * (parseFloat(item.discount_val) / 100)
            : parseFloat(item.discount_val) || 0;

        const discountedPrice = base - disc;
        const vat = discountedPrice * (parseFloat(item.vat_percent) / 100);
        const netCost = discountedPrice + vat + freight;
        return {
            net_unit_cost: netCost,
            line_total: netCost * (parseFloat(item.quantity) || 0)
        };
    };

    const totalFreight = useMemo(() => {
        return lineItems.reduce((sum, item) => sum + (parseFloat(item.freight_cost) || 0), 0);
    }, [lineItems]);

    const grandTotal = useMemo(() => {
        return lineItems.reduce((sum, item) => sum + calculateItemTotals(item).line_total, 0);
    }, [lineItems]);

    const totalPaid = useMemo(() => {
        return payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    }, [payments]);

    const overpaymentAmount = useMemo(() => {
        return totalPaid > grandTotal ? totalPaid - grandTotal : 0;
    }, [totalPaid, grandTotal]);

    const remainingBalance = useMemo(() => {
        return grandTotal - totalPaid;
    }, [grandTotal, totalPaid]);

    const updateItem = (index, field, value) => {
        const updated = [...lineItems];

        if (field === 'catentry_id') {
            const selected = catentries.find(c => c.value === parseInt(value));
            if (selected) {
                // updated[index].prev_cost = selected.data.listprice?.listprice || 0;
                updated[index].selling_price = selected.data.listprice?.listprice || 0;
                updated[index].product_name = selected.data.productname;
            }
        }

        // Special handling for promo toggle
        if (field === 'is_promo') {
            updated[index].is_promo = value;
            if (!value) {
                updated[index].promo_value = 0;
            }
            if (value) {
                updated[index].cost_price = 0;
                if (!updated[index].promo_value) {
                    updated[index].promo_value = updated[index].selling_price || 0;
                }
            }
        }
        updated[index][field] = value;
        setLineItems(updated);
    };

    const loadVendorCredit = useCallback(async (vId) => {
        if (!vId) {
            setAvailableCredit(0);
            return;
        }
        setIsFetchingCredit(true);
        try {
            const balance = await fetchVendorCredit(vId);
            // console.log(balance)
            // Ensure we handle absolute values if your API returns negative for credits
            setAvailableCredit(Math.abs(balance));
        } catch (err) {
            console.error("Failed to fetch vendor credit", err);
            setAvailableCredit(0);
        } finally {
            setIsFetchingCredit(false);
        }
    }, []);

    const applyCreditToPayment = () => {
        if (availableCredit <= 0) return;

        // Check if credit is already added to prevent duplicates
        const creditExists = payments.some(p => p.is_credit_drawdown);
        if (creditExists) {
            showToast("Credit already applied", "info");
            return;
        }

        // Determine how much credit to apply (either full credit or full grand total)
        const amountToApply = Math.min(availableCredit, grandTotal);

        const creditPayment = {
            bankaccount_id: 'CREDIT_ACCOUNT', // Logic in backend will handle this via is_credit_drawdown
            amount: amountToApply.toString(),
            is_credit_drawdown: true,
            label: "Vendor Credit"
        };

        setPayments([creditPayment, ...payments.filter(p => p.amount !== '')]);
        showToast(`Applied ${header.currency} ${amountToApply} from vendor credit`, "success");
    };

    const handleFastTrackSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const payload = {
                ...header,
                receipttype: 'ADHC',
                total_freight: totalFreight,
                line_items: lineItems.map(item => ({
                    ...item,
                    freight_cost: parseFloat(item.freight_cost) || 0,
                    ...calculateItemTotals(item),
                    is_promo: !!item.is_promo,
                    promo_value: item.is_promo ? parseFloat(item.promo_value) || 0 : null,
                })),
                // payments: header.payment_method === 'cash' ? payments : []
                payments: header.payment_method === 'cash'
                    ? payments 
                        .filter(p => p.amount !== '' && p.amount !== null && !isNaN(parseFloat(p.amount)))
                        .map(p => ({
                            ...p,
                            amount: parseFloat(p.amount)
                        }))
                    : []
            };

            // console.log(payload)

            await client.postData('/vendor/fast-track-restock', payload);
            showToast("Restock processed successfully!", "success");

            // navigate('/vendor');

            setHeader({
                store_id: '',
                vendor_id: '',
                ponumber: generateUniquePoNumber(),
                receipt_date: new Date().toISOString().substring(0, 16),
                currency: currencies[0]?.value || '',
                payment_method: 'cash',
                invoice_due_date: '',
            })
            setVendorDetails(null);
            setLineItems([{ ...initialLineItem }]);
            setPayments([{ bankaccount_id: '', amount: '' }]);

        } catch (err) {
            showToast(err.response?.data?.detail || "Process failed", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) return (
        <div className="flex flex-col items-center justify-center min-h-screen">
            <Loader2 className="animate-spin h-10 w-10 text-blue-600 mb-4" />
            <p className="text-gray-500 font-medium">Preparing Fast-Track Workflow...</p>
        </div>
    );

    return (
        <div className="p-4 sm:p-6 lg:p-8 bg-gray-100 min-h-screen">
            <div className="max-w-5xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                    <Button onClick={() => navigate('/vendor')} variant="secondary" size="sm" className="flex items-center justify-center">
                        <ChevronLeft size={18} className="mr-1" /> Back
                    </Button>
                    <div className="text-right">
                        <h1 className="text-2xl font-bold text-gray-900 flex items-center justify-end gap-2">
                            <Zap className="text-yellow-500 fill-current" size={24} /> Fast-Track Restock
                        </h1>
                        <p className="text-gray-600 font-medium">{vendorDetails?.vendorname}</p>
                    </div>
                </div>

                <form onSubmit={handleFastTrackSubmit} className="space-y-6">
                    {/* STEP 1: HEADER INFO */}
                    <Card className="p-6 border-l-4 border-l-blue-600 shadow-md">
                        <div className="grid grid-cols-1 mb-4">
                            <SearchableSelectBox
                                label="Select Vendor"
                                options={vendors.map(v => ({
                                    label: v.vendorname,
                                    value: v.vendor_id
                                }))}
                                value={header.vendor_id}
                                onChange={e => {
                                    const val = parseInt(e.target.value);
                                    const selected = vendors.find(v => v.vendor_id === val);
                                    setVendorDetails(selected);
                                    setHeader(prev => ({ ...prev, vendor_id: val }));
                                    loadVendorCredit(val);
                                }}
                                required
                                icon={<Truck size={18} className="text-blue-600" />}
                            />

                            {/* Optional: Responsive Vendor Info Badge */}
                            {vendorDetails && (
                                <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-100 flex items-center gap-3">
                                    <div className="bg-blue-600 p-2 rounded-full text-white">
                                        <Info size={16} />
                                    </div>
                                    <div className="text-xs">
                                        <p className="font-bold text-blue-900 uppercase">Contact Person</p>
                                        <p className="text-blue-700">
                                            {vendorDetails.member?.addresses?.[0]?.first_name || 'N/A'} —
                                            {vendorDetails.member?.addresses?.[0]?.phone1 || 'No Phone'}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            <SearchableSelectBox
                                label="Receiving Store"
                                options={stores}
                                value={header.store_id}
                                onChange={e => setHeader({ ...header, store_id: e.target.value })}
                                required
                                icon={<Store size={18} />}
                            />
                            <Input
                                label="System PO#"
                                value={header.ponumber}
                                readOnly
                                className="bg-gray-50 font-mono text-blue-700"
                                icon={<FileText size={18} />}
                            />
                            <DateTimeInput
                                label="Receipt Date"
                                value={header.receipt_date}
                                onChange={e => setHeader({ ...header, receipt_date: e.target.value })}
                                icon={<Calendar size={18} />}
                            />
                            <SearchableSelectBox
                                label="Currency"
                                options={currencies}
                                value={header.currency}
                                onChange={e => setHeader({ ...header, currency: e.target.value })}
                                icon={<DollarSign size={18} />}
                            />
                        </div>
                    </Card>

                    {/* STEP 2: LINE ITEMS */}
                    <div className="space-y-4">
                        <div className="flex items-center justify-between px-2">
                            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                                <Package className="text-blue-600" size={20} /> Items Received
                            </h2>
                            <span className="text-sm font-medium text-gray-500 bg-white px-3 py-1 rounded-full border shadow-sm">
                                {lineItems.length} {lineItems.length === 1 ? 'Item' : 'Items'}
                            </span>
                        </div>

                        {lineItems.map((item, idx) => (
                            <Card key={idx} className="p-6 shadow-md border border-gray-200 relative overflow-hidden">
                                {lineItems.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => setLineItems(lineItems.filter((_, i) => i !== idx))}
                                        className="absolute top-1 right-1 p-2 text-red-400 bg-red-50 hover:text-red-800 hover:bg-red-200 rounded-full transition-colors"
                                    >
                                        <Trash2 size={20} />
                                    </button>
                                )}

                                <div className="grid grid-cols-1 gap-6">
                                    {/* Row 1: Product Selection & Info */}
                                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end">
                                        <div className="lg:col-span-8">
                                            <SearchableSelectBox
                                                label={`Select Product / Service (#${idx + 1})`}
                                                options={catentries}
                                                value={item.catentry_id}
                                                onChange={e => updateItem(idx, 'catentry_id', e.target.value)}
                                                placeholder="Search by name or part number..."
                                                required
                                            />
                                        </div>
                                        <div className="lg:col-span-4 bg-gray-50 p-3 rounded-lg border border-dashed border-gray-300">
                                            <p className="text-xs text-gray-500 uppercase font-bold mb-1">Price Intel</p>
                                            <p className="text-sm font-medium text-gray-700">Last Selling Price: {header.currency} {item.selling_price}</p>
                                        </div>
                                    </div>

                                    {/* Row 2: Quantity & Costing */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                                        <Input
                                            label="Qty Received"
                                            type="number"
                                            value={item.quantity}
                                            onChange={e => updateItem(idx, 'quantity', e.target.value)}
                                            required
                                        />
                                        <Input
                                            label="Unit Cost (Raw)"
                                            type="number"
                                            value={item.cost_price}
                                            onChange={e => updateItem(idx, 'cost_price', e.target.value)}
                                            required
                                            icon={<Calculator size={16} />}
                                        />
                                        {/* <Input
                                            label="Freight"
                                            type="number"
                                            value={item.freight_cost}
                                            onChange={e => updateItem(idx, 'freight_cost', e.target.value)}
                                            icon={<Truck size={16} className="text-orange-500" />}
                                        /> */}
                                        {/* Discount Logic Stacked */}
                                        <div className="flex flex-col gap-1">
                                            <label className="text-sm font-semibold text-text-primary flex items-center gap-1">
                                                <Percent size={14} /> Discount
                                            </label>
                                            <div className="flex shadow-sm">
                                                <input
                                                    type="number"
                                                    className="w-full border border-gray-300 p-2 rounded-l-md focus:ring-1 focus:ring-blue-500 outline-none text-black"
                                                    value={item.discount_val}
                                                    onChange={e => updateItem(idx, 'discount_val', e.target.value)}
                                                />
                                                <select
                                                    className="bg-gray-100 border border-l-0 border-gray-300 p-2 rounded-r-md text-sm font-bold cursor-pointer text-black"
                                                    value={item.discount_type}
                                                    onChange={e => updateItem(idx, 'discount_type', e.target.value)}
                                                >
                                                    <option value="percent">%</option>
                                                    <option value="amount">Fix</option>
                                                </select>
                                            </div>
                                        </div>

                                        <Input
                                            label="V.A.T %"
                                            type="number"
                                            value={item.vat_percent}
                                            onChange={e => updateItem(idx, 'vat_percent', e.target.value)}
                                        />
                                    </div>

                                    {/* Row 3: Final Pricing & Logistics */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 pt-4 border-t border-gray-100">
                                        <div className="flex flex-col justify-center bg-blue-50 p-2 rounded border border-blue-100">
                                            <p className="text-[10px] uppercase font-bold text-blue-600">Final Unit Cost</p>
                                            <p className="text-lg font-black text-blue-800">
                                                {header.currency} {calculateItemTotals(item).net_unit_cost.toFixed(2)}
                                            </p>
                                        </div>
                                        <Input
                                            label="New Selling Price"
                                            type="number"
                                            value={item.selling_price}
                                            onChange={e => updateItem(idx, 'selling_price', e.target.value)}
                                        />
                                        <Input
                                            label="Expiry Date"
                                            type="date"
                                            value={item.expiry_date}
                                            onChange={e => updateItem(idx, 'expiry_date', e.target.value)}
                                        />
                                        <div className="flex flex-col justify-center text-right">
                                            <p className="text-[10px] uppercase font-bold text-gray-500">Line Total</p>
                                            <p className="text-xl font-black text-gray-900">
                                                {header.currency} {calculateItemTotals(item).line_total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-6 pt-4 border-t border-gray-100">
                                        <div className="flex items-center gap-3">
                                            <Checkbox
                                                id={`promo-${idx}`}
                                                checked={item.is_promo}
                                                onChange={(e) => updateItem(idx, 'is_promo', e.target.checked)}
                                                label="This is a Promo / Free Item"
                                            />
                                        </div>

                                        {item.is_promo && (
                                            <Input
                                                label="Promo Nominal Value (per unit)"
                                                type="number"
                                                required
                                                value={item.promo_value}
                                                onChange={e => updateItem(idx, 'promo_value', parseFloat(e.target.value) || 0)}
                                                placeholder="0.00"
                                                className="w-48"
                                            />
                                        )}
                                    </div>
                                    <TextArea
                                        label="Item Notes"
                                        rows={1}
                                        value={item.comment}
                                        onChange={e => updateItem(idx, 'comment', e.target.value)}
                                        placeholder="Serial numbers, batch info..."
                                    />
                                </div>
                            </Card>
                        ))}

                        <Button
                            type="button"
                            onClick={() => setLineItems([...lineItems, { ...initialLineItem }])}
                            variant="secondary"
                            className="w-full py-4 border-2 border-dashed border-gray-300 bg-white hover:bg-gray-50 flex items-center justify-center gap-2"
                        >
                            <Plus size={20} /> Add Another Product
                        </Button>
                    </div>

                    {/* STEP 3: FINANCIALS & PAYMENT */}
                    <Card className="p-8 bg-gray-900 text-white shadow-2xl">
                        {overpaymentAmount > 0 && (
                            <div className="mt-4 p-4 mb-4 bg-orange-900/30 border border-orange-500 rounded-lg flex items-start gap-3 animate-pulse">
                                <Info className="text-blue-400 mt-1" size={20} />
                                <div>
                                    <p className="font-bold text-gray-100">Overpayment Detected</p>
                                    <p className="text-sm text-gray-150 opacity-90">
                                        You are paying {header.currency} {overpaymentAmount.toLocaleString()} more than the stock value.
                                        This excess will be added to {vendorDetails?.vendorname}'s credit balance for future use.
                                    </p>
                                </div>
                            </div>
                        )}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                            <div>
                                <h3 className="text-xl font-bold mb-6 flex items-center gap-2 border-b border-gray-700 pb-2">
                                    <CreditCard size={20} className="text-blue-400" /> Payment & Settlement
                                </h3>

                                <div className="space-y-6">
                                    <div className="flex items-center gap-6 bg-gray-800 p-4 rounded-lg border border-gray-700">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="radio"
                                                name="payMethod"
                                                checked={header.payment_method === 'cash'}
                                                onChange={() => setHeader({ ...header, payment_method: 'cash' })}
                                                className="w-4 h-4 accent-blue-500"
                                            />
                                            <span className="font-bold">Immediate Cash/Bank</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="radio"
                                                name="payMethod"
                                                checked={header.payment_method === 'credit'}
                                                onChange={() => setHeader({ ...header, payment_method: 'credit' })}
                                                className="w-4 h-4 accent-red-500"
                                            />
                                            <span className="font-bold">Credit Purchase</span>
                                        </label>
                                    </div>

                                    {header.payment_method === 'cash' && lineItems.some(item => item.is_promo) && (
                                        <div className="mt-4 p-4 bg-amber-900/30 border border-amber-600 rounded-lg flex items-start gap-3">
                                            <AlertCircle className="text-amber-400 mt-0.5 flex-shrink-0" size={20} />
                                            <div className="text-sm">
                                                <p className="font-semibold text-amber-100">Warning: Promo Items Detected</p>
                                                <p className="text-amber-200/90">
                                                    You have marked some items as promo/free but are using Cash mode.
                                                    Consider switching to <span className="font-medium">Credit</span> for better accounting.
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {header.payment_method === 'cash' ? (
                                        <div className="space-y-4">
                                            {/* Vendor Credit Info Bar */}
                                            {availableCredit > 0 && (
                                                <div className="p-3 bg-emerald-900/40 border border-emerald-700 rounded-lg flex justify-between items-center animate-in zoom-in-95">
                                                    <div className="flex items-center gap-2">
                                                        <div className="bg-emerald-500 p-1.5 rounded-full">
                                                            <DollarSign size={14} className="text-white" />
                                                        </div>
                                                        <div>
                                                            <p className="text-xs text-emerald-200 font-bold uppercase">Available Vendor Credit</p>
                                                            <p className="text-sm font-mono">{header.currency} {availableCredit.toLocaleString()}</p>
                                                        </div>
                                                    </div>
                                                    <Button
                                                        type="button"
                                                        size="xs"
                                                        variant="primary"
                                                        className="bg-emerald-600 hover:bg-emerald-500"
                                                        onClick={applyCreditToPayment}
                                                    >
                                                        Apply Credit
                                                    </Button>
                                                </div>
                                            )}
                                            {payments.map((p, i) => (
                                                <div key={i} className={`grid grid-cols-1 sm:grid-cols-12 gap-3 items-end p-3 rounded-lg border ${p.is_credit_drawdown ? 'bg-emerald-900/30 border-emerald-500 shadow-inner' : 'bg-gray-800 border-gray-700'} `}>
                                                    <div className="sm:col-span-7">
                                                        {p.is_credit_drawdown ? (
                                                            <div className="flex flex-col gap-1">
                                                                <label className="text-[10px] uppercase font-bold text-white-400">Payment Source</label>
                                                                <div className="p-2 bg-emerald-800/50 border border-emerald-600 rounded text-emerald-100 flex items-center gap-2">
                                                                    <Zap size={14} className="text-yellow-400" />
                                                                    <span className="font-bold text-sm">Vendor Credit Balance</span>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <SearchableSelectBox
                                                                label={p.is_credit_drawdown ? "Payment Source (Credit)" : "Funding Account"}
                                                                options={bankAccounts}
                                                                value={p.bankaccount_id}
                                                                disabled={p.is_credit_drawdown} // lock credit lines
                                                                onChange={e => {
                                                                    const up = [...payments];
                                                                    up[i].bankaccount_id = e.target.value;
                                                                    setPayments(up);
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                    <div className="sm:col-span-4">
                                                        <Input
                                                            label="Amount"
                                                            type="number"
                                                            value={p.amount}
                                                            readOnly={p.is_credit_drawdown}
                                                            className={p.is_credit_drawdown ? "bg-emerald-800/20 text-emerald-50 border-emerald-600 font-mono" : ""}
                                                            onChange={e => {
                                                                const up = [...payments];
                                                                up[i].amount = e.target.value;
                                                                setPayments(up);
                                                            }}
                                                        />
                                                    </div>
                                                    <div className="sm:col-span-1 text-right">
                                                        {payments.length > 1 && (
                                                            <button
                                                                type="button"
                                                                onClick={() => setPayments(payments.filter((_, pi) => pi !== i))}
                                                                className="p-2 text-red-400 hover:text-red-300"
                                                            >
                                                                <Trash2 size={18} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                            <Button
                                                type="button"
                                                onClick={() => setPayments([...payments, { bankaccount_id: '', amount: '' }])}
                                                variant="secondary"
                                                size="xs"
                                                className="text-blue-400 border-blue-400"
                                            >
                                                + Add Payment Account
                                            </Button>
                                            {header.payment_method === 'cash' && (
                                                <div className={`mt-4 p-3 rounded-lg text-xs font-bold flex justify-between items-center ${Math.abs(remainingBalance) < 0.01
                                                    ? 'bg-blue-900/20 text-blue-400'
                                                    : remainingBalance < 0
                                                        ? 'bg-blue-900/20 text-blue-400' // Green for overpaid/credit
                                                        : 'bg-orange-900/20 text-orange-400'  // Orange for underpaid
                                                    }`}>
                                                    <span>
                                                        STATUS: {
                                                            Math.abs(remainingBalance) < 0.01
                                                                ? 'FULLY SETTLED'
                                                                : remainingBalance < 0
                                                                    ? 'OVERPAID (TO CREDIT)'
                                                                    : 'PARTIAL PAYMENT'
                                                        }
                                                    </span>
                                                    <span>
                                                        {remainingBalance < 0 ? 'OVERPAYMENT:' : 'REMAINING:'} {header.currency} {Math.abs(remainingBalance).toFixed(2)}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="space-y-4 animate-in fade-in slide-in-from-top-2">
                                            <div className="p-4 bg-red-900/30 border border-red-800 rounded-lg flex items-start gap-3">
                                                <AlertCircle className="text-red-400 mt-1" size={20} />
                                                <div>
                                                    <p className="font-bold text-red-100">Accounts Payable</p>
                                                    <p className="text-sm text-red-50 opacity-80">This total will be logged as an outstanding debt to {vendorDetails?.vendorname}.</p>
                                                </div>
                                            </div>
                                            <Input
                                                label="Invoice Due Date"
                                                type="date"
                                                value={header.invoice_due_date}
                                                onChange={e => setHeader({ ...header, invoice_due_date: e.target.value })}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex flex-col justify-between items-end gap-10">
                                <div className="w-full text-right bg-gray-800 p-8 rounded-2xl border border-gray-700 shadow-inner">
                                    <p className="text-gray-400 uppercase tracking-widest text-sm mb-2">Grand Total Payable</p>
                                    <h2 className="text-6xl font-black text-white">
                                        <span className="text-2xl font-normal text-gray-500 mr-2">{header.currency}</span>
                                        {grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                    </h2>
                                </div>
                                <Button
                                    type="submit"
                                    variant="primary"
                                    size="lg"
                                    disabled={isSubmitting}
                                    className="w-full py-6 text-2xl font-black rounded-2xl shadow-xl hover:scale-[1.02] transition-transform flex items-center justify-center gap-4"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Loader2 className="animate-spin" /> Processing...
                                        </>
                                    ) : (
                                        <>
                                            <Zap className="fill-current" /> EXECUTE RESTOCK
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </Card>
                </form>
            </div>
        </div>
    );
};

export default FastTrackRestock;